"use client"
import { Cancel01Icon } from "@hugeicons/core-free-icons"
import { HugeiconsIcon } from "@hugeicons/react"
import { AnimatePresence, motion } from "framer-motion"
import type React from "react"
import {
    createContext,
    type ReactNode,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react"
import { ANIMATION } from "@/lib/constants"
import { useOutsideClick } from "@/lib/use-outside-click"
import { cn } from "@/lib/utils"

/**
 * Context type for Modal state management
 */
interface ModalContextType {
    open: boolean
    setOpen: (open: boolean) => void
}

const ModalContext = createContext<ModalContextType | undefined>(undefined)

export const ModalProvider = ({ children }: { children: ReactNode }) => {
    const [open, setOpen] = useState(false)

    return (
        <ModalContext.Provider value={{ open, setOpen }}>
            {children}
        </ModalContext.Provider>
    )
}

/**
 * Hook to access modal context
 * Must be used within a Modal component
 * @throws Error if used outside of ModalProvider
 */
export const useModal = () => {
    const context = useContext(ModalContext)
    if (!context) {
        throw new Error("useModal must be used within a ModalProvider")
    }
    return context
}

export function Modal({ children }: { children: ReactNode }) {
    return <ModalProvider>{children}</ModalProvider>
}

export const ModalTrigger = ({
    children,
    className,
    onClick,
    ...props
}: {
    children: ReactNode
    className?: string
} & React.ButtonHTMLAttributes<HTMLButtonElement>) => {
    const { setOpen } = useModal()
    return (
        <button
            type="button"
            className={cn(
                "relative cursor-pointer overflow-hidden rounded-none p-0 text-center text-black dark:text-white",
                className
            )}
            onClick={(e) => {
                onClick?.(e)
                setOpen(true)
            }}
            {...props}
        >
            {children}
        </button>
    )
}

export const ModalBody = ({
    children,
    className,
    labelledBy,
}: {
    children: ReactNode
    className?: string
    labelledBy?: string
}) => {
    const { open } = useModal()

    // Handle body overflow with scrollbar shift compensation
    useEffect(() => {
        if (!open) return

        const scrollBarWidth =
            window.innerWidth - document.documentElement.clientWidth
        const originalOverflow = document.body.style.overflow
        const originalPaddingRight = document.body.style.paddingRight

        if (scrollBarWidth > 0) {
            document.body.style.paddingRight = `${scrollBarWidth}px`
        }
        document.body.style.overflow = "hidden"

        return () => {
            document.body.style.overflow = originalOverflow
            document.body.style.paddingRight = originalPaddingRight
        }
    }, [open])

    const modalRef = useRef<HTMLDivElement>(null)
    const { setOpen } = useModal()
    useOutsideClick(modalRef, () => setOpen(false))

    useEffect(() => {
        if (!open) return
        const previousFocus = document.activeElement
        const dialog = modalRef.current
        const focusable = () =>
            Array.from(
                dialog?.querySelectorAll<HTMLElement>(
                    'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex="0"]'
                ) ?? []
            ).filter((element) => element.getClientRects().length > 0)

        const frameId = requestAnimationFrame(() => focusable()[0]?.focus())
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                event.preventDefault()
                setOpen(false)
            }
            if (event.key !== "Tab") return
            const elements = focusable()
            const first = elements[0]
            const last = elements[elements.length - 1]
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault()
                last?.focus()
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault()
                first?.focus()
            }
        }
        document.addEventListener("keydown", handleKeyDown)
        return () => {
            cancelAnimationFrame(frameId)
            document.removeEventListener("keydown", handleKeyDown)
            if (previousFocus instanceof HTMLElement) previousFocus.focus()
        }
    }, [open, setOpen])

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: ANIMATION.duration.normal }}
                    className="fixed inset-0 z-50 flex h-full w-full items-center justify-center"
                >
                    <Overlay />

                    <motion.div
                        ref={modalRef}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={labelledBy}
                        aria-label={labelledBy ? undefined : "Details"}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{
                            duration: ANIMATION.duration.normal,
                            ease: ANIMATION.ease,
                        }}
                        className={cn(
                            "relative z-50 flex max-h-[95%] min-h-[30%] w-[95%] max-w-[95%] flex-1 flex-col overflow-hidden rounded-2xl border border-transparent bg-white sm:w-[90%] sm:max-w-[90%] md:min-h-[40%] md:w-[80%] md:max-w-[80%] lg:max-w-[60%] xl:max-w-[40%] dark:border-neutral-800 dark:bg-neutral-950",
                            className
                        )}
                        data-lenis-prevent="true"
                    >
                        <CloseIcon />
                        {children}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}

export const ModalContent = ({
    children,
    className,
}: {
    children: ReactNode
    className?: string
}) => {
    return (
        <div
            className={cn(
                "flex flex-1 flex-col p-4 sm:p-6 md:p-8 lg:p-10",
                className
            )}
        >
            {children}
        </div>
    )
}

export const ModalFooter = ({
    children,
    className,
}: {
    children: ReactNode
    className?: string
}) => {
    return (
        <div
            className={cn(
                "flex justify-end bg-gray-100 p-3 sm:p-4 dark:bg-neutral-900",
                className
            )}
        >
            {children}
        </div>
    )
}

const Overlay = ({ className }: { className?: string }) => {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: ANIMATION.duration.normal }}
            className={`fixed inset-0 z-50 h-full w-full bg-black/10 backdrop-blur-[10px] ${className}`}
        ></motion.div>
    )
}

const CloseIcon = () => {
    const { setOpen } = useModal()
    return (
        <button
            type="button"
            aria-label="Close dialog"
            onClick={() => setOpen(false)}
            className="group absolute top-3 right-3 flex items-center justify-center rounded-full bg-transparent p-1 hover:bg-muted sm:top-4 sm:right-4 sm:p-2"
        >
            <HugeiconsIcon
                icon={Cancel01Icon}
                className="size-5 text-black transition-all duration-200 dark:text-white"
                strokeWidth={1.5}
            />
        </button>
    )
}
